import { useMemo, useState } from 'react'
import { Outlet } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import {
  CreateEmployeePulseDashboardDocument,
  EmployeePulseItemFragment,
  EmployeePulseWidgetQuestionDocument,
  HomePageDocument,
} from 'generated/resources/graphql'
import compact from 'lodash/compact'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import buildUrl from 'modules/core/lib/buildUrl'
import { i18nDateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import { OnWidgetsEndLoadingParams, WidgetsProvider } from 'modules/core/lib/widgets'

import type { CommunitiesDropdown } from 'modules/dashboard/components/Groups/types'
import { buildAbsencesWidget } from 'modules/dashboard/components/Widgets/Absences'
import { buildClockInWidget } from 'modules/dashboard/components/Widgets/ClockIn'
import { buildCompanyLinksWidget } from 'modules/dashboard/components/Widgets/CompanyLinks'
import { buildDailyClockInWidget } from 'modules/dashboard/components/Widgets/DailyClockIn'
import { buildEventsWidget } from 'modules/dashboard/components/Widgets/Events'
import { buildInboxWidget } from 'modules/dashboard/components/Widgets/Inbox'
import { buildOpenPositionsWidget } from 'modules/dashboard/components/Widgets/OpenPositions'
import { WidgetDashboard } from 'modules/dashboard/components/Widgets/widgets'
import { getMomentOfDay } from 'modules/dashboard/lib/getMomentOfDay'
import CommunityHeader from 'modules/engagement/components/CommunityHeader'
import { NewEmployeePulseModal } from 'modules/engagement/components/NewEmployeePulseModal'
import WeeklyCelebrationsCarousel from 'modules/engagement/components/WeeklyCelebrationsCarousel'
import { valueToPulse } from 'modules/engagement/lib/pulse'
import { buildInboxWidget as buildNewInboxWidget } from 'modules/inbox_tasks/components/InboxWidget'
import CommunityPostList from 'modules/posts/components/CommunityPostList'
import PostsSkeleton from 'modules/posts/components/CommunityPostList/Skeleton'

type WidgetConfig = {
  shouldShowNewInboxWidget: boolean
  shouldShowCompanyLinksWidget: boolean
  shouldShowAbsencesWidget: boolean
  shouldShowClockInWidget: boolean
  shouldShowDailyClockInWidget: boolean
}

type Props = {
  widgetConfig?: WidgetConfig
  communitiesEnabled: boolean
  pulseEnabled: boolean
  pulse?: EmployeePulseItemFragment | null
  isLoading: boolean
  canCreateGroup: boolean
  timeoffModuleEnabled: boolean
}

const Dashboard = ({
  widgetConfig,
  communitiesEnabled,
  pulse,
  pulseEnabled,
  isLoading,
  canCreateGroup,
  timeoffModuleEnabled,
}: Props) => {
  const [showPulse, setShowPulse] = useState(false)
  const { employee, access, company } = useCurrentSession()

  const companyId = company.id

  const today = i18nDateToString(moment(), 'backend')

  const [selectedCommunity, setSelectedCommunity] = useState<CommunitiesDropdown>({
    title: '',
    strategy: 'all',
  })

  const args = useMemo(
    () => ({
      companyId,
      today,
      startOn: today,
      endOn: i18nDateToString(moment().endOf('year'), 'backend'),
      employeeId: employee?.id,
      locationId: employee?.locationId,
    }),
    [companyId, today, employee?.id, employee?.locationId]
  )

  const momentOfDay = getMomentOfDay()

  const title = useMemo(() => {
    const name = employee?.preferredName || access.firstName

    switch (momentOfDay) {
      case 'morning':
        return i18n.t('dashboard.header.morning', { name })
      case 'afternoon':
        return i18n.t('dashboard.header.afternoon', { name })
      case 'evening':
        return i18n.t('dashboard.header.evening', { name })
    }
  }, [momentOfDay, employee, access.firstName])

  const handleWidgetsEndLoading = ({ shownWidgets, emptyWidgets }: OnWidgetsEndLoadingParams) => {
    track(trackingEvents.dashboard.seeNewHome, {
      widgets: shownWidgets,
      empty_widgets: emptyWidgets,
    })
  }

  const [createEmployeePulse] = useMutation(CreateEmployeePulseDashboardDocument, {
    extractorFn: (data) => data?.engagementMutations?.createEngagementEmployeePulse,
  })

  const pulseOptions = useMemo(() => {
    return pulseEnabled
      ? {
          pulse: pulse?.value ? valueToPulse(pulse.value) : undefined,
          onPulseClick: () => {
            setShowPulse(true)
          },
        }
      : {}
  }, [pulse, pulseEnabled])

  const header = useMemo(
    () => ({
      title,
      employeeFirstName: employee?.preferredName || access.firstName,
      employeeLastName: access.lastName,
      employeeAvatar: access.avatar ? buildUrl(access.avatar) : undefined,
      ...pulseOptions,
    }),
    [title, employee, access.firstName, access.lastName, access.avatar, pulseOptions]
  )

  const todayAtTitle = i18n.t('new_dashboard.today_at', { company: company.name })

  const widgets = useMemo(
    () =>
      widgetConfig
        ? compact([
            widgetConfig.shouldShowDailyClockInWidget && buildDailyClockInWidget(),
            !widgetConfig.shouldShowDailyClockInWidget &&
              widgetConfig.shouldShowClockInWidget &&
              buildClockInWidget(),
            widgetConfig.shouldShowNewInboxWidget ? buildNewInboxWidget() : buildInboxWidget(),
            buildEventsWidget(),
            widgetConfig.shouldShowAbsencesWidget && buildAbsencesWidget(),
            buildOpenPositionsWidget(),
            widgetConfig.shouldShowCompanyLinksWidget && buildCompanyLinksWidget(),
          ])
        : [],
    [widgetConfig]
  )

  return (
    <WidgetsProvider widgets={widgets} onWidgetsEndLoading={handleWidgetsEndLoading}>
      <WidgetDashboard header={header} widgets={widgets} args={args}>
        <Box flexDirection="column" gap="s24" paddingTop="s8">
          <Box flexDirection="column" gap="s24">
            <h2 className="text-2xl text-f1-foreground font-semibold">{todayAtTitle}</h2>
            <WeeklyCelebrationsCarousel timeoffModuleEnabled={timeoffModuleEnabled} />
          </Box>
          {showPulse && (
            <NewEmployeePulseModal
              source="dashboard"
              onClose={() => {
                setShowPulse(false)
              }}
              onSubmit={(variables) => {
                return createEmployeePulse({
                  refetchQueries: [EmployeePulseWidgetQuestionDocument, HomePageDocument],
                  awaitRefetchQueries: true,
                  variables,
                })
              }}
              employeePulse={pulse}
            />
          )}
          {communitiesEnabled ? (
            <Box flexDirection="column" gap="s24">
              <CommunityHeader
                selectedCommunity={selectedCommunity}
                onSelectedCommunity={setSelectedCommunity}
                canCreateGroup={canCreateGroup}
              />
              <CommunityPostList selectedCommunity={selectedCommunity} />
            </Box>
          ) : (
            <>
              {isLoading && (
                <Box flexDirection="column" gap="s16">
                  <PostsSkeleton />
                </Box>
              )}
            </>
          )}
        </Box>
        <Outlet />
      </WidgetDashboard>
    </WidgetsProvider>
  )
}

export default Dashboard
